import { Component } from '@theme/component';
import { ThemeEvents, SlideshowSelectEvent, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { DialogCloseEvent } from '@theme/dialog';

/** Slides rendered with `defer_image` carry their real source in data attributes. */
const DEFERRED_IMAGE_SELECTOR = 'img[data-etolie-src]';

/** Slides that belong to the gallery carousel, excluding the zoom dialog copies. */
const SLIDESHOW_MEDIA_SELECTOR = 'slideshow-component[ref="slideshow"] slideshow-slide .product-media[data-media-id]';

/** ETOLIE 04.09.2026: odtwarzacz wideo slajdu karuzeli. Kopie z dialogu zoom tu nie należą. */
const SLIDESHOW_VIDEO_SELECTOR = 'deferred-media[autoplay]';

/**
 * Gives a deferred product image its real source.
 *
 * Slides past the first one ship a 1x1 placeholder so the gallery, not the
 * browser lazy heuristic, decides when they compete for bandwidth with the LCP
 * image: in a horizontal carousel the neighbouring slides sit well inside the
 * lazy threshold, which on a slow connection is measured in thousands of pixels.
 *
 * @param {ParentNode} root - The element whose deferred images should load.
 */
function hydrateDeferredImages(root) {
  for (const image of root.querySelectorAll(DEFERRED_IMAGE_SELECTOR)) {
    if (!(image instanceof HTMLImageElement)) continue;

    const { etolieSrc: source, etolieSrcset: sourceSet } = image.dataset;

    delete image.dataset.etolieSrc;
    delete image.dataset.etolieSrcset;

    if (sourceSet) image.srcset = sourceSet;
    if (source) image.src = source;
  }
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });

    // ETOLIE 04.09.2026: `pointerenter` pada na iOS juz przy dotknieciu ekranu
    // (motyw korzysta z tego w slideshow.js), wiec to najwczesniejszy uczciwy
    // moment na wstawienie odtwarzacza. Zamkniecie powiekszenia pauzuje KAZDE
    // deferred-media na stronie, wiec film biezacego slajdu trzeba wznowic.
    this.addEventListener('pointerenter', this.#handleGalleryPointerEnter, { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });

    // The slideshow owns drag/swipe and selection. The gallery only adds the
    // product-specific keyboard and progress contract, so there is still one
    // source of truth for the active slide.
    queueMicrotask(this.#bindGalleryControls);
    queueMicrotask(this.#startDeferredMedia);
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#progressResizeObserver?.disconnect();
    this.#progressResizeObserver = null;
    this.#settleObserver?.disconnect();
    this.#settleObserver = null;
    this.#progressScroller = null;
    if (this.#progressFrame) cancelAnimationFrame(this.#progressFrame);
    this.#progressFrame = 0;
  }

  /** @type {HTMLElement | null} */
  #progressScroller = null;

  /** @type {ResizeObserver | null} */
  #progressResizeObserver = null;

  #progressFrame = 0;

  /** @type {MutationObserver | null} */
  #settleObserver = null;

  #videoPreparationArmed = false;

  #bindGalleryControls = () => {
    const scroller = this.querySelector('slideshow-component slideshow-slides[ref="scroller"]');
    if (!(scroller instanceof HTMLElement)) return;

    const slideshow = scroller.closest('slideshow-component');
    if (slideshow instanceof HTMLElement) this.#observeCarouselSettle(slideshow);

    if (this.#progressScroller !== scroller) {
      this.#progressScroller?.removeEventListener('scroll', this.#requestProgressSync);
      this.#progressScroller?.removeEventListener('keydown', this.#handleGalleryKeyDown);
      this.#progressResizeObserver?.disconnect();
      this.#progressScroller = scroller;
      scroller.addEventListener('scroll', this.#requestProgressSync, {
        passive: true,
        signal: this.#controller.signal,
      });
      scroller.addEventListener('keydown', this.#handleGalleryKeyDown, { signal: this.#controller.signal });

      const slides = this.slideshow?.slides || [];
      if (slides.length > 1) {
        scroller.tabIndex = 0;
        scroller.setAttribute('aria-label', 'Galeria produktu');
      }

      if ('ResizeObserver' in window) {
        this.#progressResizeObserver = new ResizeObserver(this.#requestProgressSync);
        this.#progressResizeObserver.observe(scroller);
      }
    }

    this.#requestProgressSync();
  };

  /**
   * ETOLIE 04.09.2026: karuzela nie ma zdarzenia „slajd stanął". `slideshow:select`
   * leci W TRAKCIE przewijania, a po zatrzymaniu indeks już się nie zmienia, więc
   * nie leci nic — i film nigdy nie dostawał szansy na nieruchomym, gotowym
   * elemencie. Natywne `scrollend` jest międzyprzeglądarkowo dostępne dopiero od
   * końca 2025, więc na iPhonach klientek nie można na nim polegać. Zdejmowany
   * atrybut `transitioning` to ten sam sygnał końca przejścia, którego motyw
   * używa już dziś (slideshow.js #onTransitionEnd).
   *
   * @param {HTMLElement} slideshow
   */
  #observeCarouselSettle(slideshow) {
    this.#settleObserver?.disconnect();
    this.#settleObserver = new MutationObserver(() => {
      if (slideshow.hasAttribute('transitioning')) return;
      this.#startCurrentSlideVideo();
    });
    this.#settleObserver.observe(slideshow, { attributes: true, attributeFilter: ['transitioning'] });
  }

  /** ETOLIE 04.09.2026: jedno wejście „bieżący slajd ma grać". */
  #startCurrentSlideVideo() {
    const index = this.slideshow?.current ?? 0;
    this.#slidesInOrder()[index]?.querySelector(SLIDESHOW_VIDEO_SELECTOR)?.ensureAutoplay?.();
  }

  /**
   * ETOLIE 04.09.2026: odwrotność startu — slajdy poza wybranym mają stanąć.
   * Obserwator widoczności w media.js tego nie wyłapie: sąsiedni slajd karuzeli
   * nadal przecina okno w ~44 %, więc jego warunek pauzy się nie spełnia. Dotąd
   * film zatrzymywała sama przeglądarka (autoplay poza kadrem), ale jawne play()
   * — jedyna ścieżka startu na iOS — zdejmuje w WebKicie restrykcję
   * `InvisibleAutoplayNotPermitted` i ta pomoc znika.
   *
   * Bierzemy indeks ze zdarzenia `slideshow:select`, a nie ze stanu karuzeli po
   * przejściu: przy przewijaniu palcem `transitioning` zdejmowane jest kilka
   * razy, jeszcze zanim `current` dojdzie do nowego slajdu, i film bieżącego
   * slajdu dostawał wtedy pauzę tuż po starcie.
   *
   * @param {number} index - The index of the selected slide.
   */
  #releaseVideosOutside(index) {
    this.#slidesInOrder().forEach((slide, position) => {
      if (position === index) return;
      slide.querySelector(SLIDESHOW_VIDEO_SELECTOR)?.releasePlayback?.();
    });
  }

  #handleGalleryPointerEnter = () => {
    this.#videoPreparationArmed = true;
    this.#prepareVideoAround(this.slideshow?.current ?? 0);
  };

  #handleDialogClose = () => {
    // ETOLIE 04.09.2026: zoom-dialog wysyła DialogCloseEvent na window, a media.js
    // pauzuje na to każde deferred-media na stronie — w tym film bieżącego slajdu
    // galerii, którego nikt potem nie wznawiał. Mikrozadanie, bo najpierw muszą
    // wykonać się wszystkie nasłuchy pauzy tego samego zdarzenia.
    queueMicrotask(() => this.#startCurrentSlideVideo());
  };

  /**
   * ETOLIE 04.09.2026: odtwarzacz slajdu z filmem wstawiamy o jeden slajd
   * wcześniej — w tym samym oknie co odroczone zdjęcia — ale dopiero po pierwszym
   * ruchu w galerii. Klientka, która galerii nie dotknie, nie pobiera ani bajtu
   * filmu, więc LCP zostaje nietknięte. Klon niesie atrybut `autoplay`, więc start
   * robi przeglądarka, gdy slajd stanie w kadrze.
   *
   * @param {number} index - The index of the active slide.
   */
  #prepareVideoAround(index) {
    if (!this.#videoPreparationArmed) return;

    const slides = this.#slidesInOrder();
    if (!slides.length) return;

    for (const offset of [0, 1, -1]) {
      slides[index + offset]?.querySelector(SLIDESHOW_VIDEO_SELECTOR)?.prepareMedia?.();
    }
  }

  #requestProgressSync = () => {
    if (this.#progressFrame) return;

    this.#progressFrame = requestAnimationFrame(() => {
      this.#progressFrame = 0;
      this.#syncProgress();
    });
  };

  #syncProgress() {
    const scroller = this.#progressScroller;
    const slideshow = this.slideshow;
    if (!scroller || !slideshow) return;

    const slides = slideshow.slides || [];
    const total = slides.length;
    if (!total) return;

    const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const position = Math.min(maxScroll, Math.max(0, Math.abs(scroller.scrollLeft)));
    const scrollRatio = maxScroll > 0 ? position / maxScroll : 0;
    const progress = total > 1 ? 1 / total + scrollRatio * (1 - 1 / total) : 1;
    const current = Math.min(total, Math.max(1, slideshow.current + 1));

    this.style.setProperty('--etolie-pdp-progress-scale', String(progress));

    for (const controls of this.querySelectorAll('slideshow-controls')) {
      if (!controls.querySelector('.slideshow-controls__counter')) continue;

      controls.style.setProperty('--etolie-pdp-progress-scale', String(progress));
      controls.setAttribute('role', 'progressbar');
      controls.setAttribute('aria-valuemin', '1');
      controls.setAttribute('aria-valuemax', String(total));
      controls.setAttribute('aria-valuenow', String(current));
      controls.setAttribute('aria-valuetext', `Zdjęcie ${current} z ${total}`);
    }
  }

  /** @param {import('@theme/events').SlideshowSelectEvent} event */
  #handleSlideshowSelect = (event) => {
    if (event.target !== this.slideshow) return;
    this.#hydrateSlidesAround(event.detail.index);
    this.#videoPreparationArmed = true;
    this.#prepareVideoAround(event.detail.index);
    this.#releaseVideosOutside(event.detail.index);
    this.#requestProgressSync();
  };

  /**
   * Loads the deferred images of the active slide and of its direct neighbours,
   * so the next swipe always finds its photo ready.
   *
   * @param {number} index - The index of the active slide.
   */
  #hydrateSlidesAround(index) {
    const slides = this.#slidesInOrder();
    if (!slides.length) return;

    for (const offset of [0, 1, -1]) {
      const slide = slides[index + offset];
      if (slide) hydrateDeferredImages(slide);
    }
  }

  /**
   * The slides of the gallery carousel, in the order the slideshow uses.
   * Falls back to the DOM while the slideshow element has not upgraded yet, so
   * an early first hydration pass is never lost.
   *
   * @returns {Element[]}
   */
  #slidesInOrder() {
    const slides = this.slideshow?.slides;
    if (slides?.length) return slides;

    return Array.from(
      this.querySelectorAll('slideshow-component[ref="slideshow"] slideshow-slide:not([hidden])')
    );
  }

  /**
   * The first slide is the LCP candidate, so its neighbours wait for it. This is
   * the whole point of deferring them: they used to start mid-download and stole
   * bandwidth from the photo the customer is actually waiting for.
   */
  #startDeferredMedia = () => {
    const start = () => this.#hydrateSlidesAround(this.slideshow?.current ?? 0);
    const firstImage = this.querySelector('slideshow-slide img.product-media__image');

    if (!(firstImage instanceof HTMLImageElement) || firstImage.complete) {
      start();
      return;
    }

    const { signal } = this.#controller;
    firstImage.addEventListener('load', start, { once: true, signal });
    firstImage.addEventListener('error', start, { once: true, signal });
  };

  /** @param {KeyboardEvent} event */
  #handleGalleryKeyDown = (event) => {
    const slideshow = this.slideshow;
    const slides = slideshow?.slides || [];
    if (!slideshow || slides.length <= 1) return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      slideshow.previous(event);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      slideshow.next(event);
    } else if (event.key === 'Home') {
      event.preventDefault();
      void slideshow.select(0, event);
    } else if (event.key === 'End') {
      event.preventDefault();
      void slideshow.select(slides.length - 1, event);
    }
  };

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    const currentCoreMedia = this.#coreMediaSignature(this);
    const incomingCoreMedia = this.#coreMediaSignature(newMediaGallery);

    // A size-only change keeps the real product gallery intact and swaps only
    // the exact technical size card at the end. Replacing the root here used to
    // disconnect every image, slideshow observer and scroller, which produced
    // a visible flash on mobile even though the product photography did not
    // change. A genuine color/product media change still receives a fresh
    // gallery so its slideshow lifecycle starts from the correct first image.
    if (!currentCoreMedia || currentCoreMedia !== incomingCoreMedia || event.detail.data.newProduct) {
      this.replaceWith(newMediaGallery);
      return;
    }

    const slideshow = this.slideshow;
    const previousIndex = slideshow?.current ?? 0;
    const previousSlide = slideshow?.slides?.[previousIndex];
    const previousMediaId = previousSlide?.querySelector('.product-media[data-media-id]')?.dataset.mediaId || '';

    // The incoming markup carries the placeholder again, so a slide that already
    // has its photo would blink back to an empty frame. Restoring the sources in
    // the same task keeps the swap invisible.
    const loadedMedia = new Set(
      Array.from(this.querySelectorAll(SLIDESHOW_MEDIA_SELECTOR))
        .filter((media) => media instanceof HTMLElement && !media.querySelector(DEFERRED_IMAGE_SELECTOR))
        .map((media) => /** @type {HTMLElement} */ (media).dataset.mediaId)
    );

    // ETOLIE 04.09.2026: przychodzący markup niesie sam <template>, więc morph
    // usuwa wstawiony <video> i atrybut data-media-loaded. Slajd, który miał już
    // odtwarzacz, dostaje go z powrotem w tym samym zadaniu.
    const loadedVideo = new Set(
      Array.from(this.querySelectorAll(SLIDESHOW_MEDIA_SELECTOR))
        .filter((media) => media.querySelector(`${SLIDESHOW_VIDEO_SELECTOR}[data-media-loaded]`))
        .map((media) => /** @type {HTMLElement} */ (media).dataset.mediaId)
    );

    morph(this, newMediaGallery, {
      ...MORPH_OPTIONS,
      childrenOnly: false,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        return node.id || node.dataset.mediaId || undefined;
      },
    });

    for (const media of this.querySelectorAll(SLIDESHOW_MEDIA_SELECTOR)) {
      const mediaId = /** @type {HTMLElement} */ (media).dataset.mediaId;
      if (loadedMedia.has(mediaId)) hydrateDeferredImages(media);

      if (loadedVideo.has(mediaId)) media.querySelector(SLIDESHOW_VIDEO_SELECTOR)?.prepareMedia?.();
    }

    // The component roots stay mounted, so refresh their ref maps explicitly
    // and restore the active slide before the browser can paint the update.
    this.updatedCallback();
    slideshow?.updatedCallback();
    this.refs.zoomDialogComponent?.updatedCallback?.();
    this.#bindGalleryControls();

    const slides = slideshow?.slides || [];
    if (!slideshow || !slides.length) return;

    const matchingIndex = previousMediaId
      ? slides.findIndex(
          (slide) => slide.querySelector('.product-media[data-media-id]')?.dataset.mediaId === previousMediaId
        )
      : -1;
    const targetIndex = matchingIndex >= 0 ? matchingIndex : Math.min(previousIndex, slides.length - 1);

    // Server markup always marks slide zero as active. Force one synchronous,
    // non-animated selection so a shopper viewing the size card stays on the
    // equivalent card and everyone else keeps the same product image.
    slideshow.current = -1;
    void slideshow.select(targetIndex, undefined, { animate: false }).then(() => {
      this.#requestProgressSync();
      this.#startCurrentSlideVideo();
    });
  };

  /**
   * Returns the ordered non-technical media family for a gallery.
   * Technical size cards are intentionally excluded because they are the only
   * media that should differ between size variants of the same color.
   *
   * @param {Element} gallery
   * @returns {string}
   */
  #coreMediaSignature(gallery) {
    return Array.from(
      gallery.querySelectorAll(
        'slideshow-component[ref="slideshow"] slideshow-slide:not(.etolie-product-media--technical) .product-media[data-media-id]'
      ),
      (media) => media.dataset.mediaId || ''
    )
      .filter(Boolean)
      .join('|');
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
